import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, roundedPoly, shade } from './draw-utils';
import type { ProceduralDef } from '../types';

/**
 * B2 Crystal Carrier parts — docs/02-bosses.md.
 * Armored hexagon body + crystal weak point (closed/open frames).
 */
export const bossCarrierBody: ProceduralDef = {
  key: 'boss/carrier/body',
  width: 224,
  height: 224,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    const R = c - 16;
    const armor = 0x5a5246;

    // Hexagonal armored hull
    const pts: Array<[number, number]> = [];
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i;
      pts.push([c + Math.cos(a) * R, c + Math.sin(a) * R]);
    }
    roundedPoly(ctx, pts, 18);
    fillOutlined(ctx, armor, 9);

    // Top-left highlight facet
    roundedPoly(
      ctx,
      [
        [c, c],
        [c + Math.cos(Math.PI) * R * 0.92, c + Math.sin(Math.PI) * R * 0.92],
        [c + Math.cos((Math.PI * 4) / 3) * R * 0.92, c + Math.sin((Math.PI * 4) / 3) * R * 0.92],
      ],
      10,
    );
    ctx.fillStyle = css(shade(armor, 1.18));
    ctx.fill();

    // Armor plate seams
    ctx.strokeStyle = css(shade(armor, 0.62));
    ctx.lineWidth = 5;
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i + Math.PI / 6;
      ctx.beginPath();
      ctx.moveTo(c + Math.cos(a) * R * 0.4, c + Math.sin(a) * R * 0.4);
      ctx.lineTo(c + Math.cos(a) * R * 0.88, c + Math.sin(a) * R * 0.88);
      ctx.stroke();
    }

    // Corner bolts
    ctx.fillStyle = css(shade(armor, 0.5));
    for (const [x, y] of pts) {
      circle(ctx, c + (x - c) * 0.86, c + (y - c) * 0.86, 6);
      ctx.fill();
    }

    // Central socket (the crystal sprite sits on top of this)
    circle(ctx, c, c, R * 0.4);
    fillOutlined(ctx, shade(armor, 0.55), 6);
    void h;
  },
};

/**
 * Crystal weak point overlay — frame 0: closed (armored shutter),
 * frame 1: open (glowing orange crystal). Drawn at body center.
 */
export const bossCarrierCrystal: ProceduralDef = {
  key: 'boss/carrier/crystal',
  width: 112,
  height: 112,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 2,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const R = c - 12;

    if (frame === 0) {
      // Closed: iris shutter plates
      circle(ctx, c, c, R);
      fillOutlined(ctx, 0x3c362e, 6);
      ctx.strokeStyle = css(shade(0x3c362e, 0.6));
      ctx.lineWidth = 4;
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i;
        ctx.beginPath();
        ctx.moveTo(c, c);
        ctx.lineTo(c + Math.cos(a) * R * 0.95, c + Math.sin(a) * R * 0.95);
        ctx.stroke();
      }
      circle(ctx, c, c, R * 0.2);
      ctx.fillStyle = css(shade(0x3c362e, 1.2));
      ctx.fill();
    } else {
      // Open: glowing faceted crystal
      circle(ctx, c, c, R + 6);
      ctx.fillStyle = 'rgba(232, 84, 47, 0.35)';
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(c, c - R);
      ctx.lineTo(c + R * 0.85, c);
      ctx.lineTo(c, c + R);
      ctx.lineTo(c - R * 0.85, c);
      ctx.closePath();
      fillOutlined(ctx, COLORS.dangerOrange, 6);
      ctx.beginPath();
      ctx.moveTo(c, c - R);
      ctx.lineTo(c + R * 0.85, c);
      ctx.lineTo(c - R * 0.85, c);
      ctx.closePath();
      ctx.fillStyle = css(shade(COLORS.dangerOrange, 1.35));
      ctx.fill();
      circle(ctx, c - R * 0.2, c - R * 0.25, R * 0.16);
      ctx.fillStyle = 'rgba(255, 244, 230, 0.95)';
      ctx.fill();
    }
    void h;
  },
};

/**
 * Corner turret (B4) — an armored crystal pylon the Carrier drops into the
 * arena corners in phase 2. Barrel points +X; frame 1 = lens charging.
 */
export const bossCarrierTurret: ProceduralDef = {
  key: 'boss/carrier/turret',
  width: 72,
  height: 72,
  anchorX: 0.42,
  anchorY: 0.5,
  frames: 2,
  draw(ctx, w, h, frame) {
    const cx = w * 0.42;
    const cy = h / 2;
    const armor = 0x5a5246;
    // Hexagonal pylon base
    const pts: Array<[number, number]> = [];
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i + Math.PI / 6;
      pts.push([cx + Math.cos(a) * 22, cy + Math.sin(a) * 22]);
    }
    roundedPoly(ctx, pts, 5);
    fillOutlined(ctx, armor, 5);
    // Barrel
    ctx.beginPath();
    ctx.roundRect(cx + 10, cy - 6, 26, 12, 4);
    fillOutlined(ctx, shade(armor, 0.75), 4);
    // Crystal lens — glows hot while charging
    const charging = frame === 1;
    ctx.beginPath();
    ctx.moveTo(cx, cy - 11);
    ctx.lineTo(cx + 8, cy);
    ctx.lineTo(cx, cy + 11);
    ctx.lineTo(cx - 8, cy);
    ctx.closePath();
    fillOutlined(ctx, charging ? shade(COLORS.dangerOrange, 1.35) : COLORS.dangerOrange, 3);
    circle(ctx, cx - 2, cy - 3, charging ? 4 : 2.5);
    ctx.fillStyle = 'rgba(255, 244, 230, 0.95)';
    ctx.fill();
    void h;
  },
};
